/**
 * Notification provider registry (Uptime Kuma pattern).
 * Pluggable providers — add SMS, Telegram, Slack, etc. without touching checkout code.
 * Each provider implements: { name, type, enabled, send(notification) }
 */

import { supabase } from "./supabase";
import { retryInvoke } from "./supabase-retry";

// --- Provider Registry ---

const providers = new Map();

export function registerProvider(provider) {
  if (!provider.name || !provider.type || typeof provider.send !== "function") {
    throw new Error(`Invalid provider: must have name, type, and send()`);
  }
  providers.set(provider.name, { enabled: true, ...provider });
}

export function removeProvider(name) {
  providers.delete(name);
}

export function getProvider(name) {
  return providers.get(name) || null;
}

export function listProviders() {
  return Array.from(providers.values());
}

export function enableProvider(name) {
  const p = providers.get(name);
  if (p) p.enabled = true;
}

export function disableProvider(name) {
  const p = providers.get(name);
  if (p) p.enabled = false;
}

export function clearProviders() {
  providers.clear();
}

// --- Send to all enabled providers of a given type ---

export async function notify(type, notification) {
  const results = [];

  for (const [name, provider] of providers) {
    if (provider.type !== type || !provider.enabled) continue;

    try {
      await provider.send(notification);
      results.push({ provider: name, ok: true });
    } catch (err) {
      results.push({ provider: name, ok: false, error: err.message });
    }
  }

  return results;
}

// --- Convenience: send to ALL enabled providers regardless of type ---

export async function notifyAll(notification) {
  const results = [];

  for (const [name, provider] of providers) {
    if (!provider.enabled) continue;

    try {
      await provider.send(notification);
      results.push({ provider: name, ok: true });
    } catch (err) {
      results.push({ provider: name, ok: false, error: err.message });
    }
  }

  return results;
}

// --- Built-in Providers ---

export const emailProvider = {
  name: "email",
  type: "customer",
  send: async ({ to, customerName, orderId, items, subtotal, bulkDiscount, discount, shipping, total, shippingAddress, paymentMethod, paymentHandle, couponCode }) => {
    const { error } = await retryInvoke(supabase, "send-order-confirmation", {
      body: { to, customerName, orderId, items, subtotal, bulkDiscount, discount, shipping, total, shippingAddress, paymentMethod, paymentHandle, couponCode },
    });
    if (error) throw new Error(error.message);
  },
};

export const adminEmailProvider = {
  name: "admin-email",
  type: "admin",
  send: async ({ orderId, customerName, items, total, paymentMethod }) => {
    const { data, error } = await retryInvoke(supabase, "notify-order", {
      body: { orderId, customerName, items, total, paymentMethod },
    });
    if (error) throw new Error(error.message);
    // notify-order returns 200 with a JSON summary even when it did nothing
    // (see supabase/functions/notify-order/index.ts), so a real regression
    // — e.g. zero staff resolved — never surfaces as an HTTP-level `error`.
    // Treat the summary's own verdict as failure too, so it shows up here
    // in checkout the same way any other admin-notification failure does.
    if (data && data.ok === false) {
      throw new Error(data.reason || data.error || "notify-order reported failure");
    }
    if (data && typeof data.staffCount === "number" && data.staffCount === 0) {
      throw new Error("notify-order found no staff to notify");
    }
  },
};

// Telegram notifications handled server-side by notify-order edge function
// Bot token removed from client bundle for security (was exposed in DevTools)

// --- Register built-in providers ---

export function registerDefaultProviders() {
  registerProvider(emailProvider);
  registerProvider(adminEmailProvider);
}
